import { useEffect, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { testimonials } from "../data";
import { resolveAssetUrl, Section, SectionHeader } from "../shared";

const AUTO_ADVANCE_MS = 7000;

/**
 * Circular previous/next control for the testimonial carousel.
 *
 * @param {Object} props
 * @param {string} props.label - accessible label
 * @param {() => void} props.onClick - advance/rewind handler
 * @param {import("react").ReactNode} props.children - chevron icon
 * @returns {import("react").ReactElement}
 */
function ArrowButton(props) {
  const { label, onClick, children } = props;

  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="bg-ice text-cerulean hover:bg-cerulean shadow-soft inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition hover:text-white"
    >
      {children}
    </button>
  );
}

/**
 * Auto-scrolling "What Marketers Are Saying" carousel. Rotates through customer
 * quotes on a timer, pauses on hover, and exposes prev/next controls and dots.
 *
 * @returns {import("react").ReactElement}
 */
export function Testimonial() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = testimonials.length;

  useEffect(() => {
    if (paused) {
      return;
    }

    const id = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, AUTO_ADVANCE_MS);

    return () => window.clearInterval(id);
  }, [paused, count]);

  const goTo = (next) => setIndex(((next % count) + count) % count);
  const active = testimonials[index];

  return (
    <Section className="bg-crystal/60">
      <SectionHeader title="What Marketers Are Saying" />

      <div
        className="mx-auto flex max-w-4xl items-center gap-3 md:gap-6"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <ArrowButton label="Previous testimonial" onClick={() => goTo(index - 1)}>
          <ChevronLeftIcon fontSize="small" />
        </ArrowButton>

        <div
          key={index}
          className="animate-float-in min-h-[260px] flex-1 text-center md:min-h-[220px]"
        >
          <span
            aria-hidden="true"
            className="font-display text-cerulean/30 mb-1 block text-[72px] leading-none"
          >
            &ldquo;
          </span>
          <blockquote className="font-display text-ink text-[clamp(17px,2vw,22px)] leading-normal font-medium tracking-tight">
            {active.quote}
          </blockquote>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-6">
            <div className="text-center sm:text-left">
              <div className="text-ink text-sm font-semibold">{active.author}</div>
              <div className="text-muted text-xs">
                {active.role} at {active.company}
              </div>
            </div>
            <img
              src={resolveAssetUrl(active.logo)}
              alt={active.company}
              className="h-7 w-auto max-w-[150px] object-contain opacity-80"
            />
          </div>
        </div>

        <ArrowButton label="Next testimonial" onClick={() => goTo(index + 1)}>
          <ChevronRightIcon fontSize="small" />
        </ArrowButton>
      </div>

      <div className="mt-8 flex items-center justify-center gap-2">
        {testimonials.map((testimonial, idx) => (
          <button
            key={testimonial.author}
            type="button"
            aria-label={`Show testimonial from ${testimonial.author}`}
            onClick={() => goTo(idx)}
            className={`h-2 rounded-full transition-all duration-300 ${
              idx === index ? "bg-cerulean w-6" : "bg-cerulean/25 hover:bg-cerulean/50 w-2"
            }`}
          />
        ))}
      </div>
    </Section>
  );
}
